/**
 * Découpage d'un lot de télémétrie PAR SESSION D'ORIGINE (CCT-261).
 *
 * ⚠️ Module volontairement PUR — aucune dépendance à React Native, au stockage ni au réseau.
 *
 * C'est délibéré, et c'est le fond du problème qu'il corrige. Les tests de `beacon` sont des
 * « ports fidèles » de sa logique, réécrits dans le test parce que le module réel tire React
 * Native : un port peut rester vert pendant que le code réel dérive — et ce beacon existe en
 * DEUX exemplaires (app et back-office), ce qui double le risque. La règle qui décide à quelle
 * session appartient un événement vit donc ici, importable telle quelle par le test comme par
 * les deux beacons.
 */

/** Un événement en attente : seuls les champs qui servent au découpage sont décrits. */
export interface Pending {
  seq?: number;
  /** Session d'ORIGINE, posée à l'empilement. Marqueur LOCAL : retiré avant envoi. */
  _sid?: string;
}

/** Session d'origine d'un événement, ou chaîne vide s'il n'en porte pas. */
export function sidDe(e: Pending | null | undefined): string {
  const v = (e as { _sid?: unknown } | null | undefined)?._sid;
  return typeof v === 'string' && v ? v : '';
}

/**
 * Répartit un lot par session d'origine.
 *
 * Un événement SANS session est ÉCARTÉ, jamais rattaché au hasard : il ne peut venir que d'un
 * tampon écrit avant ce correctif, et le poster sous la session courante lui inventerait une
 * cause. Perdre une ligne de diagnostic est moins grave que d'en fabriquer une fausse.
 */
export function grouperParSession<T extends Pending>(batch: readonly T[]): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const e of batch) {
    const sid = sidDe(e);
    if (!sid) continue;
    const l = out.get(sid);
    if (l) l.push(e); else out.set(sid, [e]);
  }
  return out;
}

/**
 * Clé d'acquittement d'un événement envoyé.
 *
 * Elle porte la SESSION : deux sessions numérotent leurs événements à partir de 1, et un `seq`
 * seul retirerait du tampon la ligne homonyme d'une autre session, encore en attente.
 */
export function ackKey(e: Pending): string {
  return `${sidDe(e)}|${e.seq}`;
}
